/* eslint-disable sonarjs/no-nested-functions */
import React, { useEffect, useState } from 'react';
import { assertString } from '@Utils/assert';
import * as breakpoints from '@Styles/mixins/breakpoints.module.scss';

function handlerModeHOCs(media, handler, didMountHandler) {
  return function createHandlerModeHOCsWrapper(Component) {
    return function HandlerModeHOCWrapper(props) {
      useEffect(() => {
        const handleMedia = (media) => {
          handler(props, media);
        };

        media.addListener(handleMedia);
        if (didMountHandler) {
          didMountHandler(props, media);
        } else {
          handleMedia(media);
        }

        return () => {
          media.removeListener(handleMedia);
        };
      }, [media, handler, didMountHandler, props]);

      return <Component {...props} />;
    };
  };
}

function propsModeHOCs(media, breakpointPropName) {
  return function createPropsModeHOCsWrapper(Component) {
    return function PropsModeHOCWrapper(props) {
      const [breakpointState, setBreakpointState] = useState({
        [breakpointPropName]: media.matches,
      });

      useEffect(() => {
        const handleMedia = (media) => {
          setBreakpointState({ [breakpointPropName]: media.matches });
        };

        media.addListener(handleMedia);
        return () => {
          media.removeListener(handleMedia);
        };
      }, []);

      return (
        <Component {...props} {...breakpointState}>
          {props.children}
        </Component>
      );
    };
  };
}

export function withBreakpointFactory(config) {
  const { breakpoint, breakpointHOCName, breakpointPropName, mediaType = 'max-width' } = config;

  assertString(breakpoint, `breakpoint ${breakpointPropName}`);
  assertString(breakpointHOCName, 'breakpointHOCName');

  const media = window.matchMedia(`(${mediaType}: ${breakpoint})`);

  return function withBreakpointCreator({ handler, didMountHandler } = {}) {
    return function withBreakpoint(wrapper) {
      const HOC = handler ? handlerModeHOCs(media, handler, didMountHandler) : propsModeHOCs(media, breakpointPropName);
      return HOC(wrapper);
    };
  };
}

export const withTabletBreakpoint = withBreakpointFactory({
  breakpoint: breakpoints.tablet,
  breakpointPropName: 'isTablet',
  breakpointHOCName: 'withTabletBreakpoint',
});

export const withLaptopBreakpoint = withBreakpointFactory({
  breakpoint: breakpoints.laptop,
  breakpointPropName: 'isLaptop',
  breakpointHOCName: 'withLaptopBreakpoint',
});

export const withLargerThanTabletBreakpoint = withBreakpointFactory({
  breakpoint: breakpoints.tablet,
  breakpointPropName: 'isLargerThanTablet',
  breakpointHOCName: 'withLargerThanTabletBreakpoint',
  mediaType: 'min-width',
});

export const withTabletPortraitBreakpoint = withBreakpointFactory({
  breakpoint: breakpoints.tabletPortrait,
  breakpointPropName: 'isTabletPortrait',
  breakpointHOCName: 'withTabletPortraitBreakpoint',
});

export const withPhoneBreakpoint = withBreakpointFactory({
  breakpoint: breakpoints.phone,
  breakpointPropName: 'isPhone',
  breakpointHOCName: 'withPhoneBreakpoint',
});
